import { Component, inject, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Attachment, Practitioner } from 'fhir/r4';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { AsyncPipe, CommonModule, DatePipe, TitleCasePipe } from '@angular/common';
import {  HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';

import { baseStatusStyles } from '../shared/statusUIIcons';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ErrorService } from '../shared/error.service';
import { AgePipe } from "../age.pipe";
import { MatTabsModule } from '@angular/material/tabs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SuccessMessageComponent } from '../shared/success-message/success-message.component';
import { AuthService } from '../shared/auth/auth.service';
import { CodeableConcept2Pipe } from "../shared/codeable-concept2.pipe";
import { RoleSelectionDialogComponent } from './role-selection-dialog.component';
import { RejectionDialogComponent } from './rejection-dialog.component';
import { ConfirmActionDialogComponent, ConfirmActionDialogData } from '../practitioner-list/confirm-action-dialog.component';
import { authBackendEndPointToken } from '../app.config';
import { AVAILABLE_PRACTITIONER_ROLE_OPTIONS } from '../shared/system-roles';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { UploadUiComponent } from '../upload-ui/upload-ui.component';

export type PractitionerReg = Practitioner & { status: string; approvalId?: string;fhirResourceId?:string };

@Component({
    selector: 'app-practitioner-details',
    standalone: true,
    imports: [
        MatCardModule, MatButtonModule,
        AsyncPipe,
        MatDividerModule, DatePipe, CommonModule,
        TitleCasePipe, MatIconModule, AgePipe, MatTabsModule, CodeableConcept2Pipe,
        MatProgressBarModule, UploadUiComponent
    ],
    templateUrl: './practitioner-details.component.html',
    styleUrls: ['../dummy-medication-request-details/dummy-medication-request-details.component.scss', './practitioner-details.component.scss']
})
export class PractitionerDetailsComponent implements OnInit, OnChanges {

    availableRoles = AVAILABLE_PRACTITIONER_ROLE_OPTIONS;
    selectedRoles: { [key: string]: boolean } = {};
    roleConfigurations: any[] = [];

    @Input() inputData?: PractitionerReg;

    http = inject(HttpClient);
    private _sn = inject(MatSnackBar);
    backendApi = inject(authBackendEndPointToken);
    statuStyles: any;
    data: PractitionerReg | null = inject(MAT_DIALOG_DATA, { optional: true });
    dialogRef = inject(MatDialogRef<PractitionerDetailsComponent>, { optional: true });
    errorService = inject(ErrorService);
    dialog = inject(MatDialog);
    auth = inject(AuthService);

    currentRoles?: any;
    practitionerRole$ = new BehaviorSubject<any[]>([]);
    isLoading$ = new BehaviorSubject<boolean>(true);

    ngOnInit() {
        console.log('Practitioner data on init:', this.data);
        this.statuStyles = baseStatusStyles;

        if (this.data) {
            this.loadPractitionerData();
        } else {
            this.isLoading$.next(false);
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['inputData'] && !this.data && this.inputData) {
            this.data = this.inputData;
            this.loadPractitionerData();
        }
    }

    loadPractitionerData() {
        if (!this.data?.id) {
            this.isLoading$.next(false);
            return;
        }

        this.isLoading$.next(true);
        console.log('Loading practitioner data for ID:', this.data.id);

        const detailsUrl = `${this.backendApi}/api/admin/practitioners/${this.data.id}/details`;

        this.http.get<any>(detailsUrl).subscribe({
            next: (response) => {
                console.log('✅ Fetched comprehensive practitioner details:', response);

                if (response.practitioner) {
                    const unwrappedPractitioner = this.unwrapFhirResource(response.practitioner);

                    this.data = {
                        ...this.data,
                        ...unwrappedPractitioner,
                        status: this.data?.status || (unwrappedPractitioner.active ? 'active' : 'inactive'),
                        approvalId: this.data?.approvalId
                    };
                    console.log('✅ Updated this.data with unwrapped FHIR data:', this.data);
                }

                this.roleConfigurations = response.roleConfigurations || [];
                console.log('✅ Role configurations with permissions:', this.roleConfigurations);

                const mappedRoles = response.practitionerRoles?.length
                    ? response.practitionerRoles.map((pr: any) => ({
                        id: pr.id,
                        code: { text: pr.roleCode || 'no role' },
                        specialty: pr.specialty,
                        active: pr.active,
                        resource: { id: pr.id },
                        allowedServiceCategories: pr.allowedServiceCategories
                            ? pr.allowedServiceCategories.split(',')
                            : [],
                        allowedObsCategories: pr.allowedObsCategories
                            ? pr.allowedObsCategories.split(',')
                            : []
                    }))
                    : [{ code: { text: 'no role' } }];

                this.practitionerRole$.next(mappedRoles);

                this.currentRoles = mappedRoles
                    .filter((r: any) => r.code?.text && r.code.text !== 'no role')
                    .map((r: any) => r.code.text.toLowerCase());

                console.log('✅ Current roles:', this.currentRoles);
                console.log('✅ Mapped roles with permissions:', mappedRoles);

                this.initializeSelectedRoles(this.practitionerRole$.value);
                this.isLoading$.next(false);
                console.log('✅ Data loading complete');
            },
            error: (err) => {
                console.error('❌ Failed to load practitioner data:', err);
                this.errorService.openandCloseError('Failed to load practitioner data.');
                this.practitionerRole$.next([{ code: { text: 'no role' } }]);
                this.isLoading$.next(false);
            }
        });
    }

    private unwrapFhirResource(resource: any): any {
        if (!resource) return resource;

        if (typeof resource !== 'object') {
            return resource;
        }

        if (resource instanceof Date) {
            return resource;
        }

        if (Array.isArray(resource)) {
            return resource.map(item => this.unwrapFhirResource(item));
        }

        const keys = Object.keys(resource);
        if (keys.length === 1 && keys[0] === 'value') {
            return resource.value;
        }

        const unwrapped: any = {};
        for (const key in resource) {
            if (resource.hasOwnProperty(key)) {
                unwrapped[key] = this.unwrapFhirResource(resource[key]);
            }
        }

        return unwrapped;
    }
hasAnyCustomPermission(customPermissions: any): boolean {
    if (!customPermissions) return false;

    return customPermissions.canViewAllPatients ||
           customPermissions.canOrderTests ||
           customPermissions.canPrescribeMedications ||
           customPermissions.canPerformProcedures;
}
    getRolePermissions(roleName: string): any {
        const config = this.roleConfigurations.find(rc =>
            rc.roleName.toLowerCase() === roleName.toLowerCase()
        );
        return config?.permissions || null;
    }

    hasFullAccess(roleName: string): boolean {
        const config = this.roleConfigurations.find(rc =>
            rc.roleName.toLowerCase() === roleName.toLowerCase()
        );
        return config?.hasFullAccess || false;
    }

    loadPractitionerRoles() {
        this.loadPractitionerData();

    }

    initializeSelectedRoles(roles?: any[]) {
        this.selectedRoles = {};

        const currentRolesList = roles || this.practitionerRole$.value || [];
        currentRolesList.forEach((role: any) => {
            const roleText = role?.code?.text?.toLowerCase();
            if (roleText && roleText !== 'no role') {
                this.selectedRoles[roleText] = true;
            }
        });
    }

    updateRole() {
        this.initializeSelectedRoles();

        const existingConfigurations = this.buildExistingRoleConfigurations();

        const dialogRef = this.dialog.open(RoleSelectionDialogComponent, {
            width: '700px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            data: {
                availableRoles: this.availableRoles,
                selectedRoles: { ...this.selectedRoles },
                existingConfigurations: existingConfigurations,
                title: 'Update Roles & Permissions',
                message: 'Modify roles and permissions for this practitioner.'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.saveUpdatedRoles(result);
            }
        });
    }

    private buildExistingRoleConfigurations(): any[] {
        if (this.roleConfigurations && this.roleConfigurations.length > 0) {
            return this.roleConfigurations.map(rc => ({
                roleName: rc.roleName,
                specialty: rc.specialty,
                hasFullAccess: rc.hasFullAccess,
                permissions: {
                    allowedServiceRequestCategories: rc.permissions?.allowedServiceRequestCategories || [],
                    allowedObservationCategories: rc.permissions?.allowedObservationCategories || [],
                    customPermissions: {
                        canViewAllPatients: rc.permissions?.customPermissions?.canViewAllPatients || false,
                        canOrderTests: rc.permissions?.customPermissions?.canOrderTests || false,
                        canPrescribeMedications: rc.permissions?.customPermissions?.canPrescribeMedications || false,
                        canPerformProcedures: rc.permissions?.customPermissions?.canPerformProcedures || false
                    }
                }
            }));
        }

        const configurations: any[] = [];
        const currentRoles = this.practitionerRole$.value || [];
        currentRoles.forEach((role: any) => {
            if (role.code?.text && role.code.text !== 'no role') {
                configurations.push({
                    roleName: role.code.text,
                    specialty: role.specialty || undefined,
                    hasFullAccess: true,
                    permissions: {
                        allowedServiceRequestCategories: role.allowedServiceCategories || [],
                        allowedObservationCategories: role.allowedObsCategories || [],
                        customPermissions: {
                            canViewAllPatients: false,
                            canOrderTests: false,
                            canPrescribeMedications: false,
                            canPerformProcedures: false
                        }
                    }
                });
            }
        });

        return configurations;
    }

private saveUpdatedRoles(roleConfigurations: any[]) {
    if (!this.data?.id) {
        this.errorService.openandCloseError('Missing practitioner id.');
        return;
    }

    // Valid service request categories
    const validServiceRequestCodes = ['laboratory', 'imaging', 'procedure', 'surgery', 'others'];

    const updatePayload = {
        roleConfigurations: roleConfigurations.map(config => ({
            roleName: config.roleName,
            specialty: config.specialty || null,
            hasFullAccess: config.hasFullAccess,
            permissions: {
                allowedServiceRequestCategories: config.hasFullAccess
                    ? []
                    : this.filterValidServiceRequestCategories(
                          config.permissions?.allowedServiceRequestCategories || [],
                          validServiceRequestCodes
                      ),
                allowedObservationCategories: config.hasFullAccess
                    ? []
                    : (config.permissions?.allowedObservationCategories || []),
                customPermissions: {
                    canViewAllPatients: config.permissions?.customPermissions?.canViewAllPatients || false,
                    canOrderTests: config.permissions?.customPermissions?.canOrderTests || false,
                    canPrescribeMedications: config.permissions?.customPermissions?.canPrescribeMedications || false,
                    canPerformProcedures: config.permissions?.customPermissions?.canPerformProcedures || false
                }
            }
        }))
    };

    console.log('this update payload', updatePayload);

    this.http.put(`${this.backendApi}/api/admin/practitioners/${this.data.id}/roles`, updatePayload)
        .subscribe({
            next: () => {
                this._sn.openFromComponent(SuccessMessageComponent, {
                    data: { message: 'Roles updated successfully!' },
                    duration: 3000,
                    horizontalPosition: 'center',
                    verticalPosition: 'top',
                });
                 this.dialogRef?.close({
                    updated: true,
                    id: this.data?.id,
                    roleConfigurations
                });
            },
            error: (err) => {
                console.error('Update failed:', err);
                this.errorService.openandCloseError(
                    err.error?.message || 'Failed to update roles. Please try again.'
                );
            }
        });
}

/**
 * Filters service request categories to only include valid codes
 * @param categories - Array of service request categories to filter
 * @param validCodes - Array of valid category codes
 * @returns Filtered array containing only valid categories
 */
private filterValidServiceRequestCategories(
    categories: string[],
    validCodes: string[]
): string[] {
    // Convert to lowercase for case-insensitive comparison and filter
    const validCodesLower = validCodes.map(code => code.toLowerCase());
    const filtered = categories.filter(category =>
        validCodesLower.includes(category.toLowerCase())
    );

    // Remove duplicates (e.g., 'Imaging' and 'imaging')
    return [...new Set(filtered.map(cat => cat.toLowerCase()))];
}

    deleteRole(role: any) {
        let roleId = role?.id || (role?.resource && role.resource.id);
        if (!roleId) {
            this.errorService.openandCloseError('Cannot determine role ID to delete.');
            return;
        }

        if (!confirm(`Are you sure you want to remove the ${role?.code?.text || 'this'} role?`)) {
            return;
        }

        this.http.delete(`${this.backendApi}/fhir/PractitionerRole/${roleId}`).subscribe({
            next: () => {
                this._sn.open('Role deleted successfully!', 'Close', { duration: 3000 });
                this.loadPractitionerData();
            },
            error: () => {
                this.errorService.openandCloseError('Failed to delete role.');
            }
        });
    }

    onRolesUpdateComplete() {
        this._sn.openFromComponent(SuccessMessageComponent, {
            data: { message: 'Roles updated successfully!' },
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
        });
        this.loadPractitionerData();
    }

    canAddPatientRegistration(): boolean {
        return this.auth.can('patient', 'add');
    }

    approvePractitioner() {
        const id = this.data?.id;
        if (!id) {
            this.errorService.openandCloseError('Missing practitioner id.');
            return;
        }

        const dialogRef = this.dialog.open(RoleSelectionDialogComponent, {
            width: '700px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            data: {
                availableRoles: this.availableRoles,
                selectedRoles: {},
                title: 'Configure Roles for Practitioner',
                message: 'Select roles and configure permissions for this practitioner before approval.'
            }
        });

        dialogRef.afterClosed().subscribe((roleConfigurations: any[]) => {
            if (!roleConfigurations || roleConfigurations.length === 0) {
                return;
            }
            this.performApprovalWithConfigurations(id, roleConfigurations);
        });
    }

    rejectPractitioner() {
        const id = this.data?.id;
        if (!id) {
            this.errorService.openandCloseError('Missing practitioner id.');
            return;
        }

        const practitionerName = `${this.data?.name?.[0]?.given?.join(' ') || ''} ${this.data?.name?.[0]?.family || ''}`.trim()
            || 'this practitioner';

        const dialogRef = this.dialog.open(RejectionDialogComponent, {
            width: '550px',
            maxWidth: '90vw',
            disableClose: true,
            data: {
                practitionerName: practitionerName
            }
        });

        dialogRef.afterClosed().subscribe((reason: string) => {
            if (!reason) {
                return;
            }
            this.performRejection(id, reason);
        });
    }

    archivePractitioner() {
        const fhirId = this.data?.fhirResourceId;
        if (!fhirId) {
            this.errorService.openandCloseError('Missing practitioner FHIR resource ID.');
            return;
        }

        const practitionerName = `${this.data?.name?.[0]?.given?.join(' ') || ''} ${this.data?.name?.[0]?.family || ''}`.trim()
            || 'this practitioner';

        const dialogData: ConfirmActionDialogData = {
            title: 'Archive Practitioner',
            message: 'Are you sure you want to archive this practitioner? They will be deactivated and will no longer have access to the system.',
            actionType: 'archive',
            itemName: practitionerName,
            requiresReason: true,
            confirmButtonText: 'Archive',
            cancelButtonText: 'Cancel'
        };

        const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
            width: '500px',
            data: dialogData,
            disableClose: true
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.confirmed) {
                this.performArchive(fhirId, result.reason || 'No reason provided');
            }
        });
    }

    reactivatePractitioner() {
        const fhirId = this.data?.fhirResourceId;
        if (!fhirId) {
            this.errorService.openandCloseError('Missing practitioner FHIR resource ID.');
            return;
        }

        const practitionerName = `${this.data?.name?.[0]?.given?.join(' ') || ''} ${this.data?.name?.[0]?.family || ''}`.trim()
            || 'this practitioner';

        const dialogData: ConfirmActionDialogData = {
            title: 'Reactivate Practitioner',
            message: 'Are you sure you want to reactivate this practitioner? They will regain access to the system.',
            actionType: 'reactivate',
            itemName: practitionerName,
            requiresReason: false,
            confirmButtonText: 'Reactivate',
            cancelButtonText: 'Cancel'
        };

        const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
            width: '500px',
            data: dialogData,
            disableClose: true
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.confirmed) {
                this.performReactivate(fhirId);
            }
        });
    }

    private performArchive(fhirId: string, reason: string) {
        this.http.post(`${this.backendApi}/api/admin/practitioners/${fhirId}/archive`, { reason })
            .subscribe({
                next: () => {
                    this._sn.openFromComponent(SuccessMessageComponent, {
                        data: { message: 'Practitioner archived successfully' },
                        duration: 3000,
                        horizontalPosition: 'center',
                        verticalPosition: 'top',
                    });

                    if (this.data) {
                        this.data.active = false;
                    }

                    this.loadPractitionerData();

                    this.dialogRef?.close({
                        archived: true,
                        id: fhirId,
                        reason: reason
                    });
                },
                error: (err) => {
                    console.error('Archive failed:', err);
                    this.errorService.openandCloseError(
                        err.error?.message || 'Failed to archive practitioner. Please try again.'
                    );
                }
            });
    }

    private performReactivate(fhirId: string) {
        this.http.post(`${this.backendApi}/api/admin/practitioners/${fhirId}/reactivate`, {})
            .subscribe({
                next: () => {
                    this._sn.openFromComponent(SuccessMessageComponent, {
                        data: { message: 'Practitioner reactivated successfully' },
                        duration: 3000,
                        horizontalPosition: 'center',
                        verticalPosition: 'top',
                    });

                    if (this.data) {
                        this.data.active = true;
                    }

                    this.loadPractitionerData();

                    this.dialogRef?.close({
                        reactivated: true,
                        id: fhirId
                    });
                },
                error: (err) => {
                    console.error('Reactivate failed:', err);
                    this.errorService.openandCloseError(
                        err.error?.message || 'Failed to reactivate practitioner. Please try again.'
                    );
                }
            });
    }

    private performRejection(practitionerId: string, reason: string) {
        const approvalId = (this.data as any)?.approvalId;

        if (!approvalId) {
            this.http.get(`${this.backendApi}/api/admin/approvals/pending?accountType=Practitioner`)
                .subscribe({
                    next: (response: any) => {
                        const approval = response.find((a: any) => a.fhirResourceId === practitionerId);
                        if (approval) {
                            this.sendRejection(approval.id, reason, practitionerId);
                        } else {
                            this.errorService.openandCloseError('Could not find approval record for this practitioner.');
                        }
                    },
                    error: (err) => {
                        console.error('Failed to find approval:', err);
                        this.errorService.openandCloseError('Failed to find approval record.');
                    }
                });
        } else {
            this.sendRejection(approvalId, reason, practitionerId);
        }
    }

    private sendRejection(approvalId: string, reason: string, practitionerId: string) {
        const payload = { reason: reason };

        this.http.post(`${this.backendApi}/api/admin/approvals/${approvalId}/reject`, payload)
            .subscribe({
                next: () => {
                    this._sn.openFromComponent(SuccessMessageComponent, {
                        data: {
                            message: 'Practitioner registration rejected successfully.',
                            action: 'OK'
                        },
                        duration: 3000,
                        horizontalPosition: 'center',
                        verticalPosition: 'top',
                    });

                    this.dialogRef?.close({
                        approved: false,
                        rejected: true,
                        id: practitionerId,
                        reason: reason
                    });
                },
                error: (err) => {
                    console.error('Rejection failed:', err);
                    this.errorService.openandCloseError('Could not reject practitioner. Please try again.');
                }
            });
    }

    private performApprovalWithConfigurations(practitionerId: string, roleConfigurations: any[]) {
        console.log('Starting approval for practitioner FHIR ID:', practitionerId);
        console.log('Role configurations:', roleConfigurations);

        const approvalPayload = {
            roleConfigurations: roleConfigurations.map(config => ({
                roleName: config.roleName,
                specialty: config.specialty || null,
                hasFullAccess: config.hasFullAccess,
                permissions: {
                    allowedServiceRequestCategories: config.hasFullAccess
                        ? []
                        : (config.permissions?.allowedServiceRequestCategories || []),
                    allowedObservationCategories: config.hasFullAccess
                        ? []
                        : (config.permissions?.allowedObservationCategories || []),
                    customPermissions: {
                        canViewAllPatients: config.permissions?.customPermissions?.canViewAllPatients || false,
                        canOrderTests: config.permissions?.customPermissions?.canOrderTests || false,
                        canPrescribeMedications: config.permissions?.customPermissions?.canPrescribeMedications || false,
                        canPerformProcedures: config.permissions?.customPermissions?.canPerformProcedures || false
                    }
                }
            }))
        };

        console.log('Sending approval payload:', approvalPayload);

        this.http.post(`${this.backendApi}/api/admin/approvals/by-fhir-id/${practitionerId}/approve`, approvalPayload)
            .subscribe({
                next: (response: any) => {
                    console.log('Approval successful:', response);

                    this._sn.openFromComponent(SuccessMessageComponent, {
                        data: {
                            message: `Practitioner approved with ${roleConfigurations.length} role(s)!`,
                            action: 'View Details'
                        },
                        duration: 3000,
                        horizontalPosition: 'center',
                        verticalPosition: 'top',
                    });

                    if (this.data) {
                        this.data.active = true;
                    }

                    this.loadPractitionerData();

                    this.dialogRef?.close({
                        approved: true,
                        id: practitionerId,
                        roleConfigurations
                    });
                },
                error: (err) => {
                    console.error('Approval failed:', err);

                    let errorMessage = 'Failed to approve practitioner. Please try again.';

                    if (err.status === 404) {
                        errorMessage = 'No pending approval found for this practitioner.';
                    } else if (err.error?.message) {
                        errorMessage = err.error.message;
                    }

                    this.errorService.openandCloseError(errorMessage);
                }
            });
    }

    getEmailContacts() {
        return this.data?.telecom?.filter(t => t.system === 'email') || [];
    }

    getPhoneContacts() {
        return this.data?.telecom?.filter(t => t.system === 'phone') || [];
    }

    getOtherContacts() {
        return this.data?.telecom?.filter(t =>
            t.system !== 'email' && t.system !== 'phone'
        ) || [];
    }
}
